/**
 * BillingSuccessPage — Stripe success-redirect destination. Polls the
 * backend for payment status (the only way to refresh state since the
 * webhook is async), surfaces "Payment successful · membership active"
 * once paid. Idempotent — server-side already guards single-grant.
 *
 * URL contract:
 *   /billing/success?session_id=cs_test_xxx
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ACCENT = '#CBA153';

const BillingSuccessPage = () => {
  const [sp] = useSearchParams();
  const sid = sp.get('session_id');
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'checking', payment_status: null });

  useEffect(() => {
    if (!sid) {
      setState({ status: 'no_session', payment_status: null });
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await fetch(`${API}/api/billing/status/${sid}`);
        const d = await r.json();
        if (d.payment_status === 'paid') {
          setState({ status: 'paid', payment_status: 'paid', plan_id: d.plan_id });
          return;
        }
        if (d.status === 'expired') {
          setState({ status: 'expired', payment_status: d.payment_status });
          return;
        }
        if (attempts >= 6) {
          setState({ status: 'timeout', payment_status: d.payment_status });
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch (_) {
        if (attempts >= 6) setState({ status: 'error', payment_status: null });
        else timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { if (timer) clearTimeout(timer); };
  }, [sid]);

  const isPaid = state.status === 'paid';
  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: 'radial-gradient(ellipse at 50% 30%, #0B1436 0%, #05091C 60%, #01030B 100%)' }}
      data-testid="billing-success-page"
    >
      <div className="max-w-md w-full text-center">
        {isPaid ? (
          <>
            <div
              className="mx-auto mb-7 w-16 h-16 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(140deg, rgba(217, 180, 120, 0.18) 0%, rgba(141, 110, 60, 0.08) 100%)', border: `1px solid ${ACCENT}` }}
            >
              <Check size={28} style={{ color: ACCENT }} />
            </div>
            <h1
              className="text-3xl mb-4"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', color: 'rgba(245, 235, 215, 0.96)' }}
            >
              You're in.
            </h1>
            <p className="text-white/60 text-sm mb-8" style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic' }}>
              Your membership is active. Every premium feature has just unlocked.
              <br />Welcome to your Reclaim.
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 rounded-full"
              style={{
                background: 'linear-gradient(90deg, #CBA153 0%, #8E6F3D 100%)',
                color: '#0A0A0A',
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 600,
                letterSpacing: '0.14em',
                fontSize: '0.85rem',
              }}
              data-testid="success-home-cta"
            >
              Enter Reclaim →
            </button>
          </>
        ) : state.status === 'expired' || state.status === 'no_session' ? (
          <>
            <h1 className="text-2xl mb-4 text-white/85" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
              Checkout didn't complete.
            </h1>
            <p className="text-white/55 text-sm mb-7">
              No charge was made. You can try again whenever you're ready.
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="px-6 py-3 rounded-full border text-white/85"
              style={{ borderColor: 'rgba(217, 180, 120, 0.45)', fontFamily: "'Manrope', sans-serif", letterSpacing: '0.14em', fontSize: '0.85rem' }}
            >
              Back to pricing
            </button>
          </>
        ) : (
          <>
            <Loader2 size={32} className="mx-auto mb-5 animate-spin" style={{ color: ACCENT }} />
            <p className="text-white/65" style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic' }}>
              Confirming your payment…
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default BillingSuccessPage;
